import React from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { Modal, Panel, PanelGroup, designSystem } from '@yola/ws-ui';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import SearchBar from './search-bar';
import EmptyState from './empty-state';
import constants from '../constants';
import ColorSchemeSelect from './color-scheme-select';

const {
  Stack,
  Heading,
  DialogHeader,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Box,
  Paragraph,
  ColorItem,
  Accordion,
  AccordionGroup,
  AccordionHeader,
  AccordionContent,
  Divider,
} = designSystem;

const { DEFAULT_OFFSET_Y, DEFAULT_OFFSET_X, DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH } =
  constants;

const AdvancedColorsSettingsDialog = ({
  offsetX = DEFAULT_OFFSET_X,
  offsetY = DEFAULT_OFFSET_Y,
  captions,
  colorsSections,
  isVisibleHeaderTextContent,
  isAdvancedColorCustomizationAvailable,
  contentRef,
  initialSearchQuery = '',
  activeAccordionItems,
  onToggleAccordionItem,
  onColorSchemeChange,
  colorSchemeOptions,
  defaultColorSchemeOption,
  onColorSelect,
  onColorReset,
  onCancel,
  onSubmit,
  onDragEnd,
  onSearchQueryChange,
  onSearchFocus,
}) => (
  <Modal
    draggable
    fullscreen={bowser.mobile}
    width={DEFAULT_DIALOG_WIDTH}
    minWidth={DEFAULT_DIALOG_WIDTH}
    maxWidth={DEFAULT_DIALOG_WIDTH}
    height={DEFAULT_DIALOG_HEIGHT}
    offsetX={offsetX}
    offsetY={offsetY}
    dragHandleSelector=".ws-drag-trigger"
    dragCancelSelector=".ws-ui-dialog-header-controls"
    className="ws-advanced-colors-settings"
    // we have to prevent submiting dialog on overlay click if upgrade dialog is showed
    floatingModalElementSelector=".wp-dialog-overlay, .product-select-container, .ws-ui-base-select-option, .ws-ui-base-select__option"
    overlay="none"
    resizeIcon="ns-resize"
    handleCancel={onCancel}
    handleSubmit={onSubmit}
    onDragEnd={onDragEnd}
    onOverlayClick={onSubmit}
  >
    <PanelGroup height="100%" className="ws-advanced-colors-settings__panel-group">
      <Panel
        align="middle"
        corners="squared"
        className="ws-drag-trigger ws-advanced-colors-settings__flex-shrink-no"
      >
        <DialogHeader paddingBottom={isVisibleHeaderTextContent ? 'spacing-s' : 'spacing-3-xs'}>
          <div className="ws-advanced-colors-settings__header">
            <Box paddingBottom="spacing-2-xs">
              <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
            </Box>
            {isVisibleHeaderTextContent && (
              <Box paddingBottom="spacing-2-xs">
                <Heading type="heading-3">{captions.title}</Heading>
              </Box>
            )}
            <Box paddingBottom="spacing-3-xs">
              <ColorSchemeSelect
                options={colorSchemeOptions}
                defaultValue={defaultColorSchemeOption}
                onChange={onColorSchemeChange}
                preventOverflow
              />
            </Box>
            {isVisibleHeaderTextContent && <Paragraph>{captions.description}</Paragraph>}
          </div>
        </DialogHeader>
      </Panel>
      <Panel
        className="ws-advanced-colors-settings__flex-shrink-no ws-advanced-colors-settings__search-bar"
        style={{ borderBottom: 'none' }}
      >
        <SearchBar
          placeholder={captions.searchPlaceholder}
          defaultValue={initialSearchQuery}
          onChange={onSearchQueryChange}
          onClear={() => onSearchQueryChange('')}
          onFocus={onSearchFocus}
        />
      </Panel>
      <Panel
        height="100%"
        scrollable
        rootRef={contentRef}
        className="ws-advanced-colors-settings__content-wrapper"
      >
        <div className="ws-advanced-colors-settings__content">
          {colorsSections.length ? (
            <Accordion
              appearance="grey"
              defaultActiveItem={activeAccordionItems}
              onToggleItem={onToggleAccordionItem}
            >
              {colorsSections.map(({ id: variableCategory, title: sectionTitle, colorItems }) => (
                <AccordionGroup key={variableCategory} itemId={variableCategory}>
                  <AccordionHeader title={sectionTitle.toUpperCase()} isSticky />
                  <AccordionContent>
                    <Box paddingTop="spacing-m" paddingBottom="spacing-m">
                      <Stack gap="spacing-2-xs">
                        {colorItems.map(
                          (
                            { id: variableId, colorKey, isColorChanged, ...colorItemProps },
                            index
                          ) => {
                            const isLastColorItemInSection = index === colorItems.length - 1;
                            const colorItem = {
                              colorKey,
                              variableId,
                              variableCategory,
                              opacity: colorItemProps.opacity,
                            };
                            return (
                              <Box
                                key={variableId}
                                paddingRight="spacing-m"
                                paddingLeft="spacing-m"
                              >
                                <ColorItem
                                  {...colorItemProps}
                                  key={variableId}
                                  onEditClick={() => onColorSelect(colorItem)}
                                  {...(isColorChanged && {
                                    onResetClick: () => onColorReset(colorItem),
                                  })}
                                  {...(!isAdvancedColorCustomizationAvailable && {
                                    tagLabel: captions.tagLabel,
                                  })}
                                />
                                {!isLastColorItemInSection && (
                                  <Box paddingTop="spacing-2-xs">
                                    <Divider appearance="light-grey" />
                                  </Box>
                                )}
                              </Box>
                            );
                          }
                        )}
                      </Stack>
                    </Box>
                  </AccordionContent>
                </AccordionGroup>
              ))}
            </Accordion>
          ) : (
            <EmptyState title={captions.emptyStateTitle} subtitle={captions.emptyStateSubtitle} />
          )}
        </div>
      </Panel>
      <Panel
        corners="squared"
        className="ws-advanced-colors-settings__footer ws-advanced-colors-settings__flex-shrink-no"
      >
        <DialogButtonGroup>
          <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
          <DialogButtonGroupSeparator />
          <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
        </DialogButtonGroup>
      </Panel>
    </PanelGroup>
  </Modal>
);

AdvancedColorsSettingsDialog.propTypes = {
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
  captions: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(PropTypes.string).isRequired,
    tagLabel: PropTypes.string.isRequired,
    submitButton: PropTypes.string.isRequired,
    cancelButton: PropTypes.string.isRequired,
    searchPlaceholder: PropTypes.string.isRequired,
    emptyStateTitle: PropTypes.string.isRequired,
    emptyStateSubtitle: PropTypes.string.isRequired,
  }).isRequired,
  colorsSections: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      colorItems: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          color: PropTypes.string.isRequired,
          colorKey: PropTypes.string.isRequired,
          isColorChanged: PropTypes.bool.isRequired,
          opacity: PropTypes.string,
        })
      ).isRequired,
    })
  ).isRequired,
  isAdvancedColorCustomizationAvailable: PropTypes.bool.isRequired,
  isVisibleHeaderTextContent: PropTypes.bool.isRequired,
  contentRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  colorSchemeOptions: PropTypes.array.isRequired,
  defaultColorSchemeOption: PropTypes.shape().isRequired,
  initialSearchQuery: PropTypes.string,
  activeAccordionItems: PropTypes.array.isRequired,
  onToggleAccordionItem: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onColorSchemeChange: PropTypes.func.isRequired,
  onColorSelect: PropTypes.func.isRequired,
  onColorReset: PropTypes.func.isRequired,
  onSearchQueryChange: PropTypes.func.isRequired,
  onSearchFocus: PropTypes.func.isRequired,
};

export default AdvancedColorsSettingsDialog;
